import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function LeadGroups({ scope = "individual" }) {
  const isClient = scope === "institutional";
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes("admin");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", group_type: scope });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["lead-groups", scope],
    queryFn: async () => (await api.get("/lead-groups", { params: { group_type: scope } })).data,
  });

  const startAdd = () => { setEditing(null); setForm({ name: "", description: "", group_type: scope }); setOpen(true); };
  const startEdit = (g) => { setEditing(g); setForm({ name: g.name, description: g.description || "", group_type: g.group_type || scope }); setOpen(true); };

  const save = useMutation({
    mutationFn: async () => {
      if (editing) return api.patch(`/lead-groups/${editing.id}`, form);
      return api.post("/lead-groups", { ...form, group_type: scope });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead-groups", scope] });
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (gid) => api.delete(`/lead-groups/${gid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead-groups", scope] }); toast.success("Deleted"); },
  });

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">{isClient ? "Client Management" : "Lead Management"}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="groups-title">{isClient ? "Client Groups" : "Lead Groups"}</h1>
          <p className="text-sm text-slate-600 mt-1.5">{isClient ? "Named groups of institutional clients for segmentation and Kanban tracking." : "Named groups for bulk operations and segmentation (e.g. \"Mumbai HNI Q2 Campaign\")."}</p>
        </div>
        <Button onClick={startAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="add-group-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Group
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="label-eyebrow !text-slate-600">Name</th>
              <th className="label-eyebrow !text-slate-600">Description</th>
              <th className="label-eyebrow !text-slate-600 text-right">Leads</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="py-6 text-center text-sm text-slate-500">Loading…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={4} className="py-12 text-center">
                <div className="text-sm font-medium text-slate-700">No groups yet</div>
                <div className="text-xs text-slate-500 mt-1">Create one to organise leads.</div>
              </td></tr>
            ) : groups.map((g) => (
              <tr key={g.id} className="border-t border-slate-100" data-testid={`group-row-${g.id}`}>
                <td className="text-sm font-semibold text-slate-900">{g.name}</td>
                <td className="text-sm text-slate-600">{g.description || "—"}</td>
                <td className="text-right font-mono-num text-sm">{g.lead_count}</td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    <Button asChild size="sm" variant="ghost" title="View Kanban" aria-label="View Kanban" data-testid={`group-kanban-${g.id}`}>
                      <Link to={`/${isClient ? "client-groups" : "lead-groups"}/${g.id}/kanban`}><LayoutGrid className="h-3.5 w-3.5" /></Link>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(g)} data-testid={`group-edit-${g.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => { if (window.confirm("Delete this group?")) del.mutate(g.id); }} data-testid={`group-delete-${g.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Group" : "New Group"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="group-form-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1" data-testid="group-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.name.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="group-form-save">
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
